import { instance } from './provider';
import { handleResponse, handleError, CONFIG } from '../utilities';

const BASE_URL = CONFIG.sbcParcelApiUrl;

export const endpoints = {
  getProcessedParcels: `${BASE_URL}/processed-parcels`,
  uploadUPSImport: `${BASE_URL}/upload-ups-import`,
  uploadGLSImport: `${BASE_URL}/upload-gls-import`,
  uploadFBOImport: `${BASE_URL}/upload-fbo-import`,
  uploadDHLImport: `${BASE_URL}/upload-dhl-import`,
  uploadVision: `${BASE_URL}/upload-vision`,
  importFiles: `${BASE_URL}/import-files`,
  visionFiles: `${BASE_URL}/vision-files`,
  rerun: `${BASE_URL}/rerun`,
};

/**
 * `handleError` resolves with the error response rather than throwing, so a failed call would
 * otherwise read as a successful empty one — a listing that renders as "nothing here". Every
 * caller confirms the field it actually needs came back.
 */
export const requireField = <T,>(result: any, field: string): T => {
  if (result && result[field] !== undefined && result[field] !== null) return result as T;
  throw new Error(
    result?.data?.message || result?.message || 'The request did not come back as expected.'
  );
};

export type VisionFileState = 'pending' | 'processed' | 'error';

export type VisionFile = {
  fileName: string;
  state: VisionFileState;
  size: number;
  /** Rough age only — the move time for archived files, the upload time for pending ones. */
  seenAt: string;
};

export type Carrier = 'ups' | 'gls' | 'fbo' | 'dhl';

export type ImportFile = {
  name: string;
  size: number;
  /** Empty on blobs uploaded before batches were recorded. */
  batch: string;
  uploadedAt: string;
};

export type RemittanceFile = {
  Part: number;
  Url: string;
  Uploaded: boolean;
};

export type AccountsPayableItem = {
  EDIAccountNumber: string | null;
  AccountsPayableRemitFile: string | null;
  FedExACHNumber: string | null;
  FedExSettlementDate: string | null;
  APRemitInformationProvided: boolean;
  RemittanceFileURL: string | null;
  RemittanceFileUploaded: boolean;
  RemittanceFiles?: RemittanceFile[];
};

export type RerunTarget = {
  customer: string;
  displayName: string;
  week: string;
  weekDisplay: string;
  message?: string | null;
};

export type ProcessedParcel = {
  id: string;
  Customer: string;
  Week: string;
  InvoiceImportFileUrl: string;
  InvoiceImportUploaded: boolean;
  AccountsPayableRemitFile: string;
  APRemitInformationProvided: boolean;
  RemittanceFileURL: string;
  RemittanceFileUploaded: boolean;
  FedExACHNumber: string;
  FedExSettlementDate: string;
  FedExRemittanceFileURL: string;
  UPSImportUploaded: boolean;
  GLSImportUploaded: boolean;
  FBOImportUploaded: boolean;
  DHLImportUploaded: boolean;
  /** null until that week has run at least once. */
  LastRunAt: string | null;
  LastRunTrigger: string | null;
  LastRunOutcome: string | null;
  /** null on weeks uploaded before counts were recorded — show the flag alone. */
  UPSImportFileCount: number | null;
  GLSImportFileCount: number | null;
  FBOImportFileCount: number | null;
  DHLImportFileCount: number | null;
  AccountsPayable?: AccountsPayableItem[];
};

export const sbcParcelApi = {
  getProcessedParcels: (customerId: string): Promise<ProcessedParcel[]> => {
    return instance
      .get<ProcessedParcel[]>(
        `${endpoints.getProcessedParcels}?customer=${encodeURIComponent(customerId)}`
      )
      .then(handleResponse)
      .catch(handleError);
  },
  getVisionFiles: (limit?: number): Promise<{ files: VisionFile[] }> => {
    return instance
      .get<{ files: VisionFile[] }>(endpoints.visionFiles, { params: { limit } })
      .then(handleResponse)
      .catch(handleError);
  },
  getImportFiles: (
    customerId: string,
    week: string,
    carrier: Carrier
  ): Promise<{ files: ImportFile[] }> => {
    return instance
      .get<{ files: ImportFile[] }>(endpoints.importFiles, {
        params: { customer: customerId, week, carrier },
      })
      .then(handleResponse)
      .catch(handleError);
  },
  removeImportFiles: (
    customerId: string,
    week: string,
    carrier: Carrier,
    name?: string
  ): Promise<{ files: ImportFile[] }> => {
    return instance
      .delete<{ files: ImportFile[] }>(endpoints.importFiles, {
        params: { customer: customerId, week, carrier, name },
      })
      .then(handleResponse)
      .catch(handleError);
  },
  getRerunTarget: (customerId: string): Promise<RerunTarget> => {
    return instance
      .get<RerunTarget>(`${endpoints.rerun}/${encodeURIComponent(customerId)}`)
      .then(handleResponse)
      .catch(handleError);
  },
  triggerRerun: (customerId: string): Promise<RerunTarget> => {
    return instance
      .post<RerunTarget>(`${endpoints.rerun}/${encodeURIComponent(customerId)}`)
      .then(handleResponse)
      .catch(handleError);
  },
  updateProcessedParcel: (
    id: string,
    data: Partial<ProcessedParcel>
  ): Promise<{ message: string }> => {
    return instance
      .patch<{ message: string }>(`${endpoints.getProcessedParcels}/${id}`, data)
      .then(handleResponse)
      .catch(handleError);
  },
};
